// src/components/forms/layoutParser.ts

import {
    PageLayout,
    PanelBlock,
    PanelRow,
    PanelColumn,
    ParsedFormLayout
} from "../../types/interfaces";

/**
 * Attempts to parse a saved layout JSON string into a structured page layout.
 * This supports both older `root: PanelBlock` formats and modern `root: PanelBlock[]` formats.
 * 
 * Gracefully handles missing or malformed content and returns null if invalid.
 *
 * @param rawLayout - JSON string representing the saved layout
 * @returns Parsed layout structure, or null if invalid
 */
export function parsePageLayout(rawLayout: string | null | undefined): ParsedFormLayout | null {
    if (!rawLayout || typeof rawLayout !== "string") return null;

    try {
        const parsed = JSON.parse(rawLayout);

        // Support legacy layout shape: single root panel
        const legacyRoot = parsed.root && !Array.isArray(parsed.root) ? [parsed.root] : [];

        // Support new schema layout shape: array of top-level panels
        const panelArray = Array.isArray(parsed.root) ? parsed.root : legacyRoot;

        if (!panelArray.length) {
            console.warn("Layout parser: No valid root panels found.");
            return null;
        }

        // Validate and normalize each panel
        const rootPanels: PanelBlock[] = panelArray.map((rawPanel: any, i: number) => {
            return {
                id: rawPanel.id ?? `panel-${i}`,
                name: rawPanel.name ?? `Panel ${i + 1}`,
                cssClass: rawPanel.cssClass ?? "",
                columns: rawPanel.columns ?? 1,
                helpText: rawPanel.helpText ?? "",
                rows: validatePanelRowsFromControls(rawPanel.controls)
            };
        });

        const output: ParsedFormLayout = {
            rootPanels,
            customCSS: parsed.customCSS ?? parsed.css ?? "",
            customJS: parsed.customJS ?? parsed.javascript ?? ""
        };

        return output;
    } catch (err) {
        console.error("Failed to parse layout JSON:", err);
        return null;
    }
}

/**
 * Validates and normalizes each row and its columns within a panel.
 * Ensures consistent structure, and fills in empty content if missing.
 * 
 * @param rows - Raw rows array from the layout
 * @returns Cleaned list of PanelRow objects
 */
function validatePanelRows(rows: any[]): PanelRow[] {
    if (!Array.isArray(rows)) return [];

    return rows.map((row, rowIndex) => {
        const validatedColumns: PanelColumn[] = Array.isArray(row.columns)
            ? row.columns.map((col: any, colIndex: number) => ({
                content: col?.content ?? null
            }))
            : [];

        return { columns: validatedColumns };
    });
}

function validatePanelRowsFromControls(controls: any[]): PanelRow[] {
    if (!Array.isArray(controls)) return [];

    // Group controls by row number
    const rowsMap = new Map<number, PanelColumn[]>();

    controls.forEach(control => {
        const row = control.row ?? 0;
        const col = control.column ?? 0;

        const content = control.field ?? control; // unwrap field if it's inside a wrapper

        if (!rowsMap.has(row)) rowsMap.set(row, []);

        const columns = rowsMap.get(row)!;
        columns[col] = { content }; // place in column index
    });

    // Convert grouped map into ordered array of PanelRow[]
    const orderedRows = Array.from(rowsMap.entries())
        .sort(([a], [b]) => a - b) // sort by row index
        .map(([, columns]) => {
            // Ensure all columns are filled; insert nulls if gaps
            const maxIndex = columns.length - 1;
            const normalized = Array.from({ length: maxIndex + 1 }, (_, i) => columns[i] ?? { content: null });
            return { columns: normalized };
        });

    return orderedRows;
}
